import { isDefined } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import { Choices, CHOICE_TYPE } from '../../../types/choice';
import { ContentId } from '../../../types/content-block';
import { FormsConstants } from '../../../types/forms-constants';
import { FormState } from '../../state/form.state';
import { updateStateAfterContentChanges } from './form-reducer.utilities';
import { updateDropDownOptions } from '../../actions/functional-elements.actions';
import {
    DropDownOptionsFunctionalElement as DropDownOptionFunctionalElement,
    EvaluationTypes,
    FunctionTypes,
} from '../../../types/drop-down-options';
import { OptionSetGroup } from '../../../types/option-set-group';

function updateOptionSet(
    oldDropDownOptionsFunctionalElement: DropDownOptionFunctionalElement,
    props: { id: number; xmlId: string; newValue: boolean },
): OptionSetGroup[] {
    return oldDropDownOptionsFunctionalElement.optionSet.map((groupOptionSet) => {
        if (oldDropDownOptionsFunctionalElement.function === FunctionTypes.MULTI_SELECT) {
            return {
                ...groupOptionSet,
                options: groupOptionSet.options.map((option) =>
                    option.id === props.xmlId ? { ...option, selected: props.newValue } : option,
                ),
            };
        } else if (oldDropDownOptionsFunctionalElement.function === FunctionTypes.SINGLE_SELECT) {
            return {
                ...groupOptionSet,
                options: groupOptionSet.options.map((option) =>
                    option.id === props.xmlId
                        ? {
                              ...option,
                              selected: props.newValue,
                          }
                        : { ...option, selected: false },
                ),
            };
        }
        return groupOptionSet;
    });
}

export function updateDropDownOptionsReducer(
    state: Readonly<FormState>,
    props: { id: ContentId; xmlId: string; newValue: boolean },
): Readonly<FormState> {
    const oldDropDownOptionsFunctionalElement = state.participantCopies[state.selectedParticipantCopyId]
        .functionalElements[props.id] as DropDownOptionFunctionalElement;
    const updatedDropDownOptions = updateOptionSet(oldDropDownOptionsFunctionalElement, props);

    const newValueFunctionalElement: { [key: string]: Readonly<DropDownOptionFunctionalElement> } = {};
    newValueFunctionalElement[props.id] = { ...oldDropDownOptionsFunctionalElement };
    const oldChoices = oldDropDownOptionsFunctionalElement.choices;
    if (isDefined(oldChoices)) {
        const newChoices: Choices = Object.keys(oldChoices).reduce((accu, key: keyof Choices) => {
            const choice = oldChoices[key];
            const isEvaluationNegative = updatedDropDownOptions.some((dropDownOption) =>
                dropDownOption.options.some(
                    (updatedOption) => updatedOption.selected && updatedOption.evaluation === EvaluationTypes.NEGATIVE,
                ),
            );
            const isValueSelected = !updatedDropDownOptions.some((dropDownOption) =>
                dropDownOption.options.some((updatedOption) => updatedOption.selected),
            );

            return {
                ...accu,
                [key]: {
                    ...choice,
                    selected: isValueSelected
                        ? false
                        : (isEvaluationNegative && choice?.type === CHOICE_TYPE.CHOICE_NEGATIVE) ||
                          (!isEvaluationNegative && choice?.type === CHOICE_TYPE.CHOICE_POSITIVE),
                    selectedInMaster:
                        state.selectedParticipantCopyId === FormsConstants.MASTER_PARTICIPANT_ID
                            ? (isEvaluationNegative && choice?.type === CHOICE_TYPE.CHOICE_NEGATIVE) ||
                              (!isEvaluationNegative && choice?.type === CHOICE_TYPE.CHOICE_POSITIVE)
                            : choice?.selectedInMaster,
                },
            };
        }, {});
        newValueFunctionalElement[props.id] = {
            ...newValueFunctionalElement[props.id],
            choices: newChoices,
            optionSet: updatedDropDownOptions,
        };
    }
    return updateStateAfterContentChanges(state, {
        ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ...newValueFunctionalElement,
    });
}

export const dropDownOptionsReducer = [
    on<FormState, Array<typeof updateDropDownOptions>>(updateDropDownOptions, updateDropDownOptionsReducer),
];
